import React, { useEffect, useId, useRef, useState } from "react";
import { Modal } from "@/components/Modal";
import { Markdown } from "@/components/Markdown";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { RichText } from "@/components/RichText";
import { Button } from "@/components/Button";
import { hasRichFormatting, htmlToPlainText } from "@/utils/format";
import { Tooltip } from "@/components/Tooltip";

export interface TextareaProps
    extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    label?: React.ReactNode;
    labelClassName?: string;
    placeholder?: string;
    rows?: number;
    error?: string;
    required?: boolean;
    resizable?: boolean;
    fullscreenEnabled?: boolean;
    fullscreenTitle?: string;
    autoGrow?: boolean;
    maxHeight?: number;
    bare?: boolean;
    aboveInput?: React.ReactNode;
    labelRight?: React.ReactNode;
    richText?: boolean;
    richTextFullscreen?: boolean;
    fullscreenFooter?:
        | React.ReactNode
        | ((close: () => void) => React.ReactNode);
    fullscreenExtraContent?: React.ReactNode;
    inputWrapperProps?: React.HTMLAttributes<HTMLDivElement>;
    overlayContent?: React.ReactNode;
    rounded?: boolean;
}

export const Textarea: React.FC<TextareaProps> = ({
    value,
    onChange,
    label,
    labelClassName = "",
    placeholder,
    rows = 3,
    error,
    required = false,
    className = "",
    resizable = true,
    fullscreenEnabled = true,
    fullscreenTitle,
    autoGrow = false,
    maxHeight = 180,
    bare = false,
    aboveInput,
    labelRight,
    richText = false,
    richTextFullscreen = false,
    fullscreenFooter,
    fullscreenExtraContent,
    inputWrapperProps,
    overlayContent,
    rounded = false,
    ...props
}) => {
    const generatedId = useId();
    const fieldId = props.id || generatedId;
    const { t } = useTranslation("global");
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [originRect, setOriginRect] = useState<DOMRect | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const effectiveMaxHeight = Math.max(maxHeight, (rows ?? 3) * 24);

    const adjustHeight = (): void => {
        const el = textareaRef.current;
        if (!el || !autoGrow) {
            return;
        }

        const minHeight = 40;
        if (!value.trim()) {
            el.style.height = `${minHeight}px`;
            el.style.lineHeight = "38px";
            return;
        }

        el.style.height = `${minHeight}px`;
        el.style.lineHeight = el.scrollHeight > minHeight ? "1.25rem" : "38px";
        el.style.height = `${Math.max(minHeight, Math.min(el.scrollHeight, effectiveMaxHeight))}px`;
    };

    useEffect(() => {
        adjustHeight();
    }, [value, autoGrow, effectiveMaxHeight]);

    const handleOpenFullscreen = (): void => {
        if (!props.disabled) {
            if (containerRef.current) {
                setOriginRect(containerRef.current.getBoundingClientRect());
            }
            setIsFullscreen(true);
        }
    };
    const handleCloseFullscreen = (): void => setIsFullscreen(false);

    const handleChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
        onChange(event);
        adjustHeight();
    };

    const baseTextareaClass = `w-full px-3.5 py-2.5 bg-white dark:bg-dark-box border border-slate-200 dark:border-white/20 rounded-xl text-xs text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-0 outline-none shadow-xs ${error ? "!border-rose-400 !bg-rose-50/50 dark:!bg-rose-950/20" : ""} ${className}`;

    // RichText onChange adapter
    const handleRichTextChange = (html: string): void => {
        const syntheticEvent = {
            target: { value: html },
            currentTarget: { value: html },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);
    };

    const textarea = (fullscreen: boolean) => {
        const useRich = richText || (richTextFullscreen && fullscreen);

        if (useRich) {
            return (
                <RichText
                    value={value}
                    onChange={handleRichTextChange}
                    placeholder={placeholder}
                    className={fullscreen ? "h-full" : className}
                    minHeight={fullscreen ? "100%" : `${(rows ?? 3) * 24}px`}
                    disabled={props.disabled || props.readOnly}
                    autoFocus={fullscreen}
                    hideFullscreen={fullscreen}
                    rounded={rounded}
                    onFullscreenClick={
                        !fullscreen ? handleOpenFullscreen : undefined
                    }
                />
            );
        }

        const plainVal =
            !useRich && value && /<\/?(?:p|div|br)\b/i.test(value)
                ? htmlToPlainText(value)
                : value;

        return (
            <textarea
                id={fieldId}
                aria-invalid={Boolean(error) || undefined}
                aria-describedby={error ? `${fieldId}-error` : undefined}
                ref={textareaRef}
                value={plainVal}
                onChange={fullscreen ? onChange : handleChange}
                placeholder={placeholder}
                rows={rows}
                autoFocus={fullscreen}
                className={`${baseTextareaClass} ${fullscreen ? "h-full rounded-none !border-0 !shadow-none !ring-0 text-sm resize-none" : resizable ? "resize-y" : "resize-none"}`}
                {...props}
            />
        );
    };

    const isRich = hasRichFormatting(value);
    const clearFormatting = () => {
        handleRichTextChange(htmlToPlainText(value).trim());
    };
    const inlineContent = richText ? (
        textarea(false)
    ) : richTextFullscreen && isRich ? (
        <div className="relative">
            <div
                role="button"
                tabIndex={props.disabled ? -1 : 0}
                onClick={handleOpenFullscreen}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleOpenFullscreen();
                    }
                }}
                className={`${baseTextareaClass} cursor-text overflow-auto pr-10`}
                style={{
                    maxHeight: `${maxHeight}px`,
                    minHeight: `${rows * 24}px`,
                }}
            >
                <Markdown className="richtext-editor text-xs">{value}</Markdown>
            </div>
            <button
                type="button"
                disabled={props.disabled || props.readOnly}
                onClick={clearFormatting}
                title={t("editor.clear_formatting")}
                className="absolute right-2 top-2 p-1 rounded text-slate-500 hover:bg-slate-100 cursor-pointer"
            >
                <Icon value="eraser" size={15} />
            </button>
        </div>
    ) : (
        textarea(false)
    );

    if (bare) {
        return (
            <>
                <div ref={containerRef} className="w-full">
                    {inlineContent}
                    {error && (
                        <p
                            id={`${fieldId}-error`}
                            className="mt-1 text-xs text-rose-600 dark:text-rose-400 font-medium"
                        >
                            {error}
                        </p>
                    )}
                </div>

                <Modal
                    isOpen={isFullscreen}
                    onClose={handleCloseFullscreen}
                    title={
                        fullscreenTitle ??
                        (typeof label === "string" ? label : t("common.editor"))
                    }
                    isFullscreen
                    onFullscreenChange={handleCloseFullscreen}
                    effect="fade"
                    initialRect={originRect}
                    getOriginRect={() =>
                        containerRef.current?.getBoundingClientRect() ??
                        originRect
                    }
                    zIndex={120000}
                    bodyClassName="p-4 min-h-0 flex flex-col overflow-hidden"
                    hideFullscreen
                    hideClose={richTextFullscreen}
                    headerLeftActions={
                        richTextFullscreen ? (
                            isRich ? (
                                <Button
                                    type="button"
                                    onClick={() => {
                                        clearFormatting();
                                        handleCloseFullscreen();
                                    }}
                                    title={t("editor.clear_formatting")}
                                    size="sm"
                                    variant="primaryinverted"
                                    className="py-2.5!"
                                >
                                    <Icon value="eraser" size={14} />
                                    {t("editor.clear_formatting")}
                                </Button>
                            ) : (
                                <span />
                            )
                        ) : undefined
                    }
                    headerActions={
                        richTextFullscreen ? (
                            <Button
                                type="button"
                                onClick={handleCloseFullscreen}
                                title={t("developments.detail.minimize")}
                                variant="custom"
                                size="none"
                                className="p-1 rounded-lg text-white transition-colors cursor-pointer bg-transparent shadow-none"
                                icon={<Icon value="minimize" size={18} />}
                            />
                        ) : undefined
                    }
                    footer={
                        typeof fullscreenFooter === "function"
                            ? fullscreenFooter(handleCloseFullscreen)
                            : fullscreenFooter || (
                                  <button
                                      type="button"
                                      className="btn secondary min-w-[120px] justify-center"
                                      onClick={handleCloseFullscreen}
                                  >
                                      {t("modal.close")}
                                  </button>
                              )
                    }
                    hideFooter={false}
                >
                    <div className="flex-1 min-h-0 flex flex-col">
                        {textarea(true)}
                    </div>
                    {fullscreenExtraContent && (
                        <div className="w-full shrink-0 pt-2">
                            {fullscreenExtraContent}
                        </div>
                    )}
                </Modal>
            </>
        );
    }

    return (
        <>
            <div ref={containerRef} className="w-full">
                {(label || labelRight || fullscreenEnabled) && (
                    <div
                        className={`flex items-end mb-0 ${label ? "justify-between" : "justify-end"}`}
                    >
                        {label && (
                            <label
                                htmlFor={fieldId}
                                className={`text-xs font-bold text-slate-700 dark:text-zinc-300 pb-0.5 ${labelClassName}`}
                            >
                                {label}{" "}
                                {required && (
                                    <span className="text-primary">*</span>
                                )}
                            </label>
                        )}
                        <div className="flex items-center gap-1">
                            {labelRight}
                            {fullscreenEnabled && (
                                <Tooltip
                                    title={t("developments.detail.fullscreen")}
                                >
                                    <button
                                        type="button"
                                        onClick={handleOpenFullscreen}
                                        className="p-1 rounded-full text-slate-500 dark:text-zinc-400 hover:text-primary transition-all cursor-pointer flex items-center justify-center"
                                    >
                                        <Icon value="maximize" size={16} />
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                )}

                <div className="relative" {...inputWrapperProps}>
                    {overlayContent}
                    {aboveInput}
                    {inlineContent}
                </div>

                {error && (
                    <p
                        id={`${fieldId}-error`}
                        className="mt-1 text-xs text-rose-600 dark:text-rose-400 font-medium"
                    >
                        {error}
                    </p>
                )}
            </div>

            <Modal
                isOpen={isFullscreen}
                onClose={handleCloseFullscreen}
                title={
                    fullscreenTitle ??
                    (typeof label === "string" ? label : t("common.editor"))
                }
                isFullscreen
                onFullscreenChange={handleCloseFullscreen}
                effect="fade"
                initialRect={originRect}
                getOriginRect={() =>
                    containerRef.current?.getBoundingClientRect() ?? originRect
                }
                zIndex={120000}
                bodyClassName="p-4 min-h-0 flex flex-col overflow-hidden"
                hideFullscreen
                hideClose={richTextFullscreen}
                headerLeftActions={
                    richTextFullscreen ? (
                        isRich ? (
                            <Button
                                type="button"
                                onClick={() => {
                                    clearFormatting();
                                    handleCloseFullscreen();
                                }}
                                title={t("editor.clear_formatting")}
                                size="sm"
                                variant="primaryinverted"
                                className="py-2.5!"
                            >
                                <Icon value="eraser" size={14} />
                                {t("editor.clear_formatting")}
                            </Button>
                        ) : (
                            <span />
                        )
                    ) : undefined
                }
                headerActions={
                    richTextFullscreen ? (
                        <Button
                            type="button"
                            onClick={handleCloseFullscreen}
                            title={t("developments.detail.minimize")}
                            variant="custom"
                            size="none"
                            className="p-1 rounded-lg text-white transition-colors cursor-pointer bg-transparent shadow-none"
                            icon={<Icon value="minimize" size={18} />}
                        />
                    ) : undefined
                }
                footer={
                    typeof fullscreenFooter === "function"
                        ? fullscreenFooter(handleCloseFullscreen)
                        : fullscreenFooter || (
                              <button
                                  type="button"
                                  className="btn secondary min-w-[120px] justify-center"
                                  onClick={handleCloseFullscreen}
                              >
                                  {t("modal.close")}
                              </button>
                          )
                }
                hideFooter={false}
            >
                <div className="flex-1 min-h-0 flex flex-col">
                    {textarea(true)}
                </div>
                {fullscreenExtraContent && (
                    <div className="w-full shrink-0 pt-2">
                        {fullscreenExtraContent}
                    </div>
                )}
            </Modal>
        </>
    );
};

export default Textarea;
